import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import { useAuth } from '../../../Contexts/AuthContext'
import { formatLastUpdated } from '../../../services/functions'
import { useNavigate } from 'react-router-dom'
import { createStoreForCollection } from '../../../Storage/createStoreForCollection'
import { useInView } from 'react-intersection-observer'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import FeelItemCard from '../FeelItemCard/FeelItemCard'
import FeelAddItem from '../FeelAddItem/FeelAddItem'
import Loader from '../../GeneralComponents/Loader'
import EditableNameInput from '../../GeneralComponents/EditableNameInput/EditableNameInput'
import ImageUploader from '../ImageUploader/ImageUploader'
import CollectionModal from '../CollectionModal/CollectionModal'
import Tooltip from '../../GeneralComponents/Tooltip'
import PropTypes from 'prop-types'
import { ButtonOutline } from '../../GeneralComponents/ButtonOutline/ButtonOutline'
import errorToast from '../../Toasts/ErrorToast'
import successToast from '../../Toasts/SuccessToast'
import Prompt from '../../DashboardComponents/Prompt/Prompt'
import ExplicitTag from '../../GeneralComponents/ExplicitTag'
import classes from './index.module.css'

export default function Collection({
  name,
  location,
  collectionId,
  collectionLength,
  feelsCount,
  isPublic,
  isExplicit,
  keywords: initialKeywords,
  onDeleteCollection,
  isFetchingCollections,
  headerText,
  dateUpdated,
}) {
  const pageLimit = 20
  const feelsLimit = 250
  const STALE_CHECK_DEBOUNCE_MS = 5000
  const navigate = useNavigate()
  const [ref, inView] = useInView()
  const queryClient = useQueryClient()
  const { getCurrentUser } = useAuth()
  const {
    getCollection,
    getCollectionById,
    getCollectionImageUrl,
    getCollectionHeaderImageUrl,
    publishToggleCollection,
    updateCollection,
    updateCollectionPartial,
    submitReport,
  } = useAPI()

  const [isOpenAddModal, setIsOpenAddModal] = useState(false)
  const [isStaleDataPromptOpen, setIsStaleDataPromptOpen] = useState(false)
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)
  const [serverFeelsCount, setServerFeelsCount] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0) // Increment to force fresh fetch
  const pendingRefreshKey = useRef(null) // Track which refreshKey we're waiting for
  const initialDateUpdated = useRef(dateUpdated)

  // zustand storage
  const useCollectionStore = createStoreForCollection(
    collectionId,
    name,
    location
  )
  const feels = useCollectionStore(state => state.feels)
  const setFeels = useCollectionStore(state => state.setFeels)
  const collectionName = useCollectionStore(state => state.collectionName)
  const setCollectionName = useCollectionStore(state => state.setCollectionName)
  const collectionNameInfo = useCollectionStore(
    state => state.collectionNameInfo
  )
  const setCollectionNameInfo = useCollectionStore(
    state => state.setCollectionNameInfo
  )
  const collectionLocation = useCollectionStore(
    state => state.collectionLocation
  )
  const setCollectionLocation = useCollectionStore(
    state => state.setCollectionLocation
  )
  const collectionLocationInfo = useCollectionStore(
    state => state.collectionLocationInfo
  )
  const setCollectionLocationInfo = useCollectionStore(
    state => state.setCollectionLocationInfo
  )
  const showSave = useCollectionStore(state => state.showSave)
  const setShowSave = useCollectionStore(state => state.setShowSave)
  const removedFeels = useCollectionStore(state => state.removedFeels)
  const setRemovedFeels = useCollectionStore(state => state.setRemovedFeels)
  const isChangedFeels = useCollectionStore(state => state.isChangedFeels)
  const setIsChangedFeels = useCollectionStore(state => state.setIsChangedFeels)

  const collectionDescription = useCollectionStore(state => state.collectionDescription)
  const setCollectionDescription = useCollectionStore(state => state.setCollectionDescription)
  const collectionDescriptionInfo = useCollectionStore(state => state.collectionDescriptionInfo)
  const setCollectionDescriptionInfo = useCollectionStore(state => state.setCollectionDescriptionInfo)
  const backdropImage = useCollectionStore(state => state.backdropImage)
  const setBackdropImage = useCollectionStore(state => state.setBackdropImage)
  const collectionKeywords = useCollectionStore(state => state.collectionKeywords)
  const setCollectionKeywords = useCollectionStore(state => state.setCollectionKeywords)
  const collectionKeywordsInfo = useCollectionStore(state => state.collectionKeywordsInfo)
  const setCollectionKeywordsInfo = useCollectionStore(state => state.setCollectionKeywordsInfo)

  const [isDragging, setIsDragging] = useState(false)
  const [forcedFetch, setForcedFetch] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const [coverImage, setCoverImage] = useState(null)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [keywordInput, setKeywordInput] = useState('')
  const descriptionRef = useRef(null)
  const skipNextImageSync = useRef(false)
  const lastSaveTime = useRef(null) // Track when we last saved to avoid false stale prompts
  const pendingImageSync = useRef(false) // Track if we need to sync after image upload
  // Initialize keywords from prop
  useEffect(() => {
    if (initialKeywords && !collectionKeywordsInfo.initialValue) {
      setCollectionKeywords(initialKeywords)
      setCollectionKeywordsInfo({ isChanged: false, initialValue: initialKeywords })
    }
  }, [initialKeywords, collectionKeywordsInfo.initialValue, setCollectionKeywords, setCollectionKeywordsInfo])

  // Keep initialDateUpdated in sync with prop when no unsaved changes
  // Only sync when dateUpdated prop is NEWER than our ref (don't downgrade after save)
  useEffect(() => {
    if (!showSave && dateUpdated && dateUpdated !== initialDateUpdated.current) {
      // Only update if prop is newer - prevents race condition where stale prop
      // overwrites the fresh date we just got from saving
      const propDate = new Date(dateUpdated)
      if (isNaN(propDate.getTime())) return
      const refDate = initialDateUpdated.current ? new Date(initialDateUpdated.current) : null
      if (!refDate || propDate > refDate) {
        initialDateUpdated.current = dateUpdated
      }
    }
  }, [dateUpdated, showSave])

  const {
    data,
    isError,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['collection', collectionId, isPublic, refreshKey],
    queryFn: ({ pageParam = 1 }) =>
      getCollection(collectionId, pageParam, pageLimit),
    getNextPageParam: pageData => {
      const nextPage = pageData.p + 1
      const newPage =
        pageData.p * pageLimit < pageData.feels_count ? nextPage : undefined
      return newPage
    },
    enabled: feelsCount > 0,
  })

  useEffect(() => {
    if (hasNextPage && feelsCount > 0 && !isError && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, feelsCount, isError, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (forcedFetch && hasNextPage && feelsCount > 0 && !isFetchingNextPage) {
      fetchNextPage()
    }
    if (!hasNextPage) {
      setDisabledButton(false)
    }
  }, [hasNextPage, forcedFetch, isFetchingNextPage])

  useEffect(() => {
    if (data && feelsCount > 0) {
      const paginatedFeels = data.pages.flatMap(page => page.feels);

      // Check if this is data from the refreshKey we're waiting for
      const isAwaitedRefresh = pendingRefreshKey.current !== null && refreshKey === pendingRefreshKey.current;

      if (isAwaitedRefresh) {
        // This is the fresh data we were waiting for - replace everything
        setFeels(paginatedFeels);
        setIsRefreshing(false);
        pendingRefreshKey.current = null;
      } else if (!isRefreshing) {
        // Normal case: add new feels that don't already exist
        const newFeels = paginatedFeels.filter(
          paginatedFeel =>
            !feels.some(existingFeel => existingFeel.f_id === paginatedFeel.f_id) &&
            !removedFeels.includes(paginatedFeel.f_id)
        );

        if (newFeels.length > 0) {
          setFeels([...feels, ...newFeels]);
        }
      }
      // If isRefreshing but not awaited refresh, ignore (old query data)

      // Skip image sync for one cycle after save so optimistic blob URLs
      // aren't overwritten by stale CDN URLs from the refetch
      if (skipNextImageSync.current) {
        skipNextImageSync.current = false
      } else {
        if (Object.keys(data.pages[0].image).length) {
          setCoverImage(data.pages[0].image?.url);
        }
        if (data.pages[0].header?.background?.url) {
          setBackdropImage(data.pages[0].header.background.url)
        }
      }

      if (data.pages[0].header?.text) {
        setCollectionDescription(data.pages[0].header.text)
        if (!collectionDescriptionInfo.initialValue) {
          setCollectionDescriptionInfo({
            ...collectionDescriptionInfo,
            initialValue: data.pages[0].header.text,
          })
        }
      }
    }
    // Also clear refreshing if feelsCount is 0 (empty collection)
    if (isRefreshing && feelsCount === 0) {
      setIsRefreshing(false);
      pendingRefreshKey.current = null;
    }
  }, [data, isRefreshing, feelsCount, refreshKey]);

  useEffect(() => {
    if (isChangedFeels || collectionLocationInfo.isSelected) {
      setShowSave(true)
    } else {
      setShowSave(false)
    }
  }, [isChangedFeels, collectionLocationInfo.isSelected])

  // Helper to trigger a fresh data fetch
  const executeRefresh = useCallback(async (freshDateUpdated, freshData = null) => {
    setIsRefreshing(true)
    initialDateUpdated.current = freshDateUpdated
    setFeels([])
    setRemovedFeels([])
    setIsChangedFeels(false)
    // Update name/description from fresh data if provided
    if (freshData?.name) {
      setCollectionName(freshData.name)
      setCollectionNameInfo({ isChanged: false, initialValue: freshData.name })
    }
    if (freshData?.header?.text !== undefined) {
      setCollectionDescription(freshData.header.text || '')
      setCollectionDescriptionInfo({ isChanged: false, initialValue: freshData.header.text || '' })
    }
    pendingRefreshKey.current = refreshKey + 1
    setRefreshKey(prev => prev + 1)
    await queryClient.invalidateQueries({ queryKey: ['collection', collectionId], exact: true })
  }, [refreshKey, collectionId, queryClient, setFeels, setRemovedFeels, setIsChangedFeels, setCollectionName, setCollectionNameInfo, setCollectionDescription, setCollectionDescriptionInfo])

  // Page Visibility API - check for stale data when user returns to tab
  const checkForStaleData = useCallback(async () => {
    // Skip if save confirm modal is already open - user is already dealing with a conflict
    if (isSaveConfirmOpen) return
    // Skip if we saved very recently - server may still be processing
    if (lastSaveTime.current && Date.now() - lastSaveTime.current < STALE_CHECK_DEBOUNCE_MS) return
    try {
      const freshData = await getCollectionById(collectionId)
      // If we just uploaded an image, silently sync our ref without prompting
      if (pendingImageSync.current) {
        pendingImageSync.current = false
        initialDateUpdated.current = freshData.date_updated
        return
      }
      if (freshData.date_updated !== initialDateUpdated.current) {
        if (showSave) {
          setIsStaleDataPromptOpen(true)
        } else {
          await executeRefresh(freshData.date_updated, freshData)
        }
      }
    } catch (err) {
      console.warn('Stale data check failed:', err)
    }
  }, [collectionId, showSave, isSaveConfirmOpen, getCollectionById, executeRefresh])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForStaleData()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkForStaleData])

  async function handleTitleSave(newName) {
    try {
      await updateCollectionPartial(collectionId, { name: newName })
      await syncDateAfterSave()
      setCollectionNameInfo({ isChanged: false, initialValue: newName })
      // Invalidate both parent query (for breadcrumb) and list query
      queryClient.invalidateQueries({ queryKey: ['collection', collectionId] })
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      successToast('Title saved')
    } catch (e) {
      errorToast(`Error saving title: ${e.message ?? ''}`)
      setCollectionName(collectionNameInfo.initialValue || name)
      setCollectionNameInfo({ ...collectionNameInfo, isChanged: false })
    }
  }

  async function handleDescriptionSave(newDescription) {
    try {
      await updateCollectionPartial(collectionId, {
        header: { header_text: newDescription },
      })
      await syncDateAfterSave()
      setCollectionDescriptionInfo({
        isChanged: false,
        initialValue: newDescription,
      })
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      successToast('Description saved')
    } catch (e) {
      errorToast(`Error saving description: ${e.message ?? ''}`)
      setCollectionDescription(collectionDescriptionInfo.initialValue || '')
      setCollectionDescriptionInfo({
        ...collectionDescriptionInfo,
        isChanged: false,
      })
    }
  }

  async function handleKeywordsSave(newKeywords) {
    try {
      await updateCollectionPartial(collectionId, { keywords: newKeywords })
      await syncDateAfterSave()
      setCollectionKeywordsInfo({ isChanged: false, initialValue: newKeywords })
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      successToast('Keywords saved')
    } catch (e) {
      errorToast(`Error saving keywords: ${e.message ?? ''}`)
      setCollectionKeywords(collectionKeywordsInfo.initialValue || [])
      setCollectionKeywordsInfo({ ...collectionKeywordsInfo, isChanged: false })
    }
  }

  function handleAddKeyword() {
    const keyword = keywordInput.trim().toLowerCase()
    if (!keyword || collectionKeywords.includes(keyword)) {
      setKeywordInput('')
      return
    }
    const newKeywords = [...collectionKeywords, keyword]
    setCollectionKeywords(newKeywords)
    setKeywordInput('')
    handleKeywordsSave(newKeywords)
  }

  function handleRemoveKeyword(keywordToRemove) {
    const newKeywords = collectionKeywords.filter(k => k !== keywordToRemove)
    setCollectionKeywords(newKeywords)
    handleKeywordsSave(newKeywords)
  }

  function getFeelsFIDs(feels) {
    return feels.map(feel => feel?.f_id).filter(Boolean)
  }

  // Sync date ref after save to prevent false "updated elsewhere" prompts
  async function syncDateAfterSave() {
    lastSaveTime.current = Date.now()
    try {
      const freshData = await getCollectionById(collectionId)
      initialDateUpdated.current = freshData.date_updated
    } catch {
      // If fetch fails, rely on lastSaveTime to skip stale checks
    }
  }

  function handleClickOnFeel(feel) {
    window.open('/feel/' + feel.f_id, '_blank')
  }

  function handleOnDragStart() {
    setIsDragging(true)
    setIsEdit(true)
  }

  function handleOnDragEnd(result) {
    setIsDragging(false)
    setIsEdit(false)
    setForcedFetch(true)

    if (!result.destination) {
      return
    }
    const items = Array.from(feels)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)
    setIsChangedFeels(true)
    const isOrderChanged = !items.every((item, index) => item === feels[index])
    if (isOrderChanged) {
      setIsChangedFeels(true)
    } else {
      setIsChangedFeels(false)
    }
    setFeels(items)
  }

  function handleOnDeleteFeel(feelToDelete) {
    const newFeels = feels.filter(feel => feel !== feelToDelete)
    setFeels(newFeels)
    setRemovedFeels([...removedFeels, feelToDelete.f_id])
    setIsChangedFeels(true)
  }

  function setToCollection(feel) {
    setFeels([...feels, feel])
    setIsChangedFeels(true)
  }

  function handleMoveToFront(feelToMove) {
    const newFeels = feels.filter(feel => feel.f_id !== feelToMove.f_id)
    setFeels([feelToMove, ...newFeels])
    setIsChangedFeels(true)
  }

  function handleMoveToEnd(feelToMove) {
    const newFeels = feels.filter(feel => feel.f_id !== feelToMove.f_id)
    setFeels([...newFeels, feelToMove])
    setIsChangedFeels(true)
  }

  // SAVE COLLECTION

  async function saveCollection() {
    setDisabledButton(true)
    try {
      await updateCollection(
        collectionId,
        collectionName,
        isPublic,
        getFeelsFIDs(feels)
      )
      await syncDateAfterSave()

      queryClient.invalidateQueries({
        queryKey: ['collection', collectionId, isPublic],
      })
      queryClient.invalidateQueries({ queryKey: ['collections'] })

      setCollectionLocationInfo({ isSelected: false, initialValue: null })
      setRemovedFeels([])
      setIsChangedFeels(false)
      successToast('Collection saved successfully')
    } catch (e) {
      errorToast(`Error saving collection: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setDisabledButton(false)
    }
  }

  async function onClickSave() {
    setDisabledButton(true)
    try {
      // Fetch fresh data to check if server changed since page load
      const freshData = await getCollectionById(collectionId)
      if (freshData.date_updated !== initialDateUpdated.current) {
        // Server has changed - show stale data warning with counts
        setServerFeelsCount(freshData.feels_count)
        setIsSaveConfirmOpen(true)
      } else {
        // No conflicts - save directly
        await saveCollection()
      }
    } catch (error) {
      console.error('Failed to fetch fresh data:', error)
      // Can't verify, save anyway
      await saveCollection()
    }
  }

  async function onConfirmSave() {
    setIsSaveConfirmOpen(false)
    // After acknowledging stale data, save directly
    await saveCollection()
  }

  function onCancelSave() {
    setIsSaveConfirmOpen(false)
    setDisabledButton(false)
  }

  // Stale data handlers
  async function onRefreshStaleData() {
    setIsStaleDataPromptOpen(false)
    setCollectionLocationInfo({ isSelected: false, initialValue: null })
    const freshData = await getCollectionById(collectionId)
    await executeRefresh(freshData.date_updated, freshData)
  }

  function onKeepLocalChanges() {
    setIsStaleDataPromptOpen(false)
  }

  // NEEDS SYNC REPORT
  async function handleNeedsSync(feel) {
    try {
      const currentUser = getCurrentUser()
      await submitReport({
        report_type: 'NEEDS_SYNC',
        report_text: '',
        object: 'feels',
        object_f_id: feel.f_id,
        report_origin: 'FAST',
        user_f_id: currentUser?.userId || '',
        report_user_id: currentUser?.email || '',
        department: 'Feels Factory',
        report_data: {
          collection_id: collectionId,
          collection_name: collectionName,
        },
      })
      successToast('Needs Sync report submitted')
    } catch (e) {
      errorToast(`Error submitting report: ${e.message ?? ''}`)
    }
  }

  // PUBLISH/UNPUBLISH COLLECTION
  function onClickPublish(isPublic) {
    setDisabledButton(true)
    publishToggleCollectionMutation.mutate(isPublic)
  }

  const publishToggleCollectionMutation = useMutation({
    mutationFn: isPublic => publishToggleCollection(collectionId, isPublic),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      queryClient.invalidateQueries({ queryKey: ['collection', collectionId] })
      setDisabledButton(false)
    },
    onSettled: e => {
      setDisabledButton(false)
    },
  })

  const allFeelsLoaded =
    !isRefreshing && (feelsCount === 0 || (!isLoading && !hasNextPage && !isFetchingNextPage))

  return (
    <>
      {isStaleDataPromptOpen && (
        <Prompt
          title="Collection was updated elsewhere"
          subtitle="Someone else made changes to this collection. Get the latest version (your local changes will be lost and you'll need to redo them), or keep your version and replace theirs when you save... (Yes, engineering is working on a better solution)"
          primaryAction="Get Latest Version"
          onPrimaryClick={onRefreshStaleData}
          secondaryAction="Keep My Version"
          onSecondaryClick={onKeepLocalChanges}
          isSecondaryActionDestructive={true}
        />
      )}
      {isSaveConfirmOpen && (
        <Prompt
          title={`Save collection with ${feels.length} Feels?`}
          subtitle={`Someone may have saved changes since you last refreshed. Saving will override the live collection (${serverFeelsCount} Feels) with your version (${feels.length} Feels).`}
          primaryAction="Cancel"
          onPrimaryClick={onCancelSave}
          secondaryAction="Save"
          onSecondaryClick={onConfirmSave}
        />
      )}
      <div
        className={classes.collectionHolder}
        style={{ position: 'relative' }}
      >
        {!allFeelsLoaded && (
          <div className={classes.loadingOverlay}>
            <Loader isLoading={true} />
          </div>
        )}
        <div style={{ pointerEvents: allFeelsLoaded ? 'auto' : 'none' }}>
          <div className={classes.headerRow}>
            <div className={classes.headerLeft}>
              <div className={classes.collectionHeader}>
                {!isError ? (
                  <div className={classes.collectionNameContainer}>
                    {isOpenAddModal && (
                      <CollectionModal
                        setToNewCollection={setToCollection}
                        closeModal={() => setIsOpenAddModal(false)}
                        reservedFeels={getFeelsFIDs(feels)}
                      />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <EditableNameInput
                        initialValue={collectionNameInfo.initialValue}
                        setInitialValue={state =>
                          setCollectionNameInfo({
                            ...collectionNameInfo,
                            initialValue: state,
                          })
                        }
                        setInitialFlag={state =>
                          setCollectionNameInfo({
                            ...collectionNameInfo,
                            isChanged: state,
                          })
                        }
                        onSave={handleTitleSave}
                        collectionId={collectionId}
                        setIsEdit={setIsEdit}
                        name={collectionName}
                        setName={setCollectionName}
                      />
                      <span className={isPublic ? classes.publicIndicator : classes.privateIndicator}></span>
                      {isExplicit && <ExplicitTag size="small" />}
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}
              </div>

              <div className={classes.collectionDescription}>
                {isEditingDescription ? (
                  <textarea
                    ref={descriptionRef}
                    className={classes.descriptionTextarea}
                    value={collectionDescription}
                    onChange={(e) => setCollectionDescription(e.target.value)}
                    onBlur={() => {
                      setIsEditingDescription(false)
                      const initialVal = collectionDescriptionInfo.initialValue
                      if (collectionDescription !== (initialVal || '')) {
                        handleDescriptionSave(collectionDescription)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setIsEditingDescription(false)
                    }}
                    placeholder="Add a collection description..."
                    rows={2}
                    autoFocus
                  />
                ) : (
                  <span
                    className={classes.descriptionDisplay}
                    onDoubleClick={() => setIsEditingDescription(true)}
                    title="Double-click to edit"
                  >
                    {collectionDescription || 'Add a collection description...'}
                  </span>
                )}
              </div>

              <div className={classes.keywordsSection}>
                <div className={classes.keywordsLabel}>Keywords</div>
                <div className={classes.keywordsRow}>
                  {collectionKeywords.map(keyword => (
                    <span key={keyword} className={classes.keywordBubble}>
                      {keyword}
                      <span
                        className={classes.keywordDelete}
                        onClick={() => handleRemoveKeyword(keyword)}
                      >
                        &times;
                      </span>
                    </span>
                  ))}
                  <input
                    className={classes.keywordInput}
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddKeyword()
                      }
                    }}
                    placeholder="Add keyword..."
                    maxLength={40}
                  />
                </div>
              </div>

              <div className={classes.collectionStats}>
                {allFeelsLoaded
                  ? `${feels.length} Feels`
                  : `${feels.length} / ${feelsCount} Feels`
                }
                {dateUpdated && `\u00A0\u00A0·\u00A0\u00A0Last updated ${formatLastUpdated(dateUpdated)}`}
              </div>
            </div>

            <div className={classes.thumbnailRow}>
              <div className={classes.thumbnailGroup}>
                <div className={classes.thumbnailLabelRow}>
                  <span className={classes.thumbnailLabel}>Cover Image</span>
                  <ImageUploader
                    collectionId={collectionId}
                    isPublic={isPublic}
                    currentImage={coverImage}
                    onUploadComplete={(blobUrl) => {
                      skipNextImageSync.current = true
                      setCoverImage(blobUrl)
                      lastSaveTime.current = Date.now()
                      pendingImageSync.current = true // Sync date on next tab return
                      queryClient.invalidateQueries({ queryKey: ['collections'] })
                      successToast('Cover image saved')
                    }}
                    iconOnly
                    title="Cover Image"
                    subtitle="Upload and attach an image file to be the cover for this collection. The image must be at least 720px wide."
                    accept="image/png, image/jpeg, image/jpg"
                    buttonText="Save Cover"
                    getUploadUrl={(id, file) => getCollectionImageUrl(id, file.type, file.size)}
                  />
                </div>
                <div className={classes.thumbnailContainerCover}>
                  {coverImage ? (
                    <img
                      className={classes.thumbnailImage}
                      src={coverImage}
                      alt="Cover"
                    />
                  ) : (
                    <div className={classes.thumbnailPlaceholder}>No cover</div>
                  )}
                </div>
              </div>

              <div className={classes.thumbnailGroup}>
                <div className={classes.thumbnailLabelRow}>
                  <span className={classes.thumbnailLabel}>Gradient</span>
                  <ImageUploader
                    collectionId={collectionId}
                    isPublic={isPublic}
                    currentImage={backdropImage}
                    onUploadComplete={(blobUrl) => {
                      skipNextImageSync.current = true
                      setBackdropImage(blobUrl)
                      lastSaveTime.current = Date.now()
                      pendingImageSync.current = true // Sync date on next tab return
                      queryClient.invalidateQueries({ queryKey: ['collections'] })
                      successToast('Gradient saved')
                    }}
                    iconOnly
                    title="Gradient"
                    subtitle="Upload a transparent PNG for the collection gradient. The image must be at least 720px wide."
                    accept="image/png"
                    buttonText="Upload Gradient"
                    formatValidator={(file) => {
                      if (file.type !== 'image/png') {
                        errorToast('Gradient images must be PNG format.')
                        return false
                      }
                      return true
                    }}
                    getUploadUrl={(id, file) => getCollectionHeaderImageUrl(id, file.type, file.size, 'background')}
                  />
                  <Tooltip text="Gradient must be a transparent PNG so header text remains visible in the app">
                    <span className={classes.helpIcon}>?</span>
                  </Tooltip>
                </div>
                <div className={classes.thumbnailContainerGradient}>
                  {backdropImage ? (
                    <img
                      className={classes.thumbnailImage}
                      src={backdropImage}
                      alt="Gradient"
                    />
                  ) : (
                    <div className={classes.thumbnailPlaceholder}>No gradient</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={classes.collectionButtons}>
            <ButtonOutline
              color={'pink'}
              size="small"
              text={'Add Feels'}
              onClick={() => setIsOpenAddModal(true)}
            />
            {!isPublic && (
              <ButtonOutline
                color={'green'}
                size="small"
                text={'Make Live in App'}
                disable={
                  isLoading ||
                  disabledButton ||
                  isFetchingCollections ||
                  isFetching ||
                  isFetchingNextPage ||
                  isError
                }
                onClick={() => onClickPublish(true)}
              />
            )}
            {isPublic && (
              <ButtonOutline
                color={'red'}
                size="small"
                text={'Hide in App'}
                disable={
                  isLoading ||
                  disabledButton ||
                  isFetchingCollections ||
                  isFetching ||
                  isFetchingNextPage ||
                  isError
                }
                onClick={() => onClickPublish(false)}
              />
            )}
            <div className={classes.collectionStatusButtons}>
              {(showSave || disabledButton) && (
                <ButtonOutline
                  color={disabledButton ? 'pink' : 'green'}
                  size="small"
                  text={disabledButton ? 'Saving...' : 'SAVE'}
                  disable={
                    isLoading ||
                    disabledButton ||
                    isFetchingCollections ||
                    isFetching ||
                    isFetchingNextPage ||
                    isError
                  }
                  onClick={onClickSave}
                />
              )}
            </div>
          </div>
          <div
            className={classes.collectionCarousel}
            style={{ height: '500px' }}
          >
            <DragDropContext
              onDragEnd={handleOnDragEnd}
              onDragStart={handleOnDragStart}
            >
              <Droppable droppableId="feels" direction="horizontal">
                {provided => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ display: 'flex' }}
                  >
                    {!isLoading &&
                      feels?.length > 0 &&
                      feels.map((feel, index) => (
                        <Draggable
                          key={feel.f_id}
                          draggableId={feel.f_id}
                          index={index}
                        >
                          {provided => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <FeelItemCard
                                isDragging={isDragging}
                                feel={feel}
                                onClick={handleClickOnFeel}
                                onDelete={handleOnDeleteFeel}
                                onMoveToFront={handleMoveToFront}
                                onMoveToEnd={handleMoveToEnd}
                                onNeedsSync={handleNeedsSync}
                                count={index + 1}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {!isLoading && !isError && feels.length < feelsLimit && (
              <div ref={ref}>
                <FeelAddItem
                  setIsEdit={setIsEdit}
                  isDragging={isDragging}
                  reservedFeels={getFeelsFIDs(feels)}
                  setToNewCollection={setToCollection}
                  disable={isFetchingNextPage}
                />
              </div>
            )}

            {isError && (
              <h1 className={classes.errorText}>
                {'Oops, something went wrong'}
              </h1>
            )}
          </div>
        </div>
      </div>

    </>
  )
}

Collection.propTypes = {
  name: PropTypes.string,
  location: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]),
  collectionId: PropTypes.string,
  collectionLength: PropTypes.number,
  feelsCount: PropTypes.number,
  isPublic: PropTypes.bool,
  isExplicit: PropTypes.bool,
  keywords: PropTypes.arrayOf(PropTypes.string),
  onDeleteCollection: PropTypes.func,
  isFetchingCollections: PropTypes.bool,
  headerText: PropTypes.string,
  dateUpdated: PropTypes.string,
}
